import { Box, styled } from '@mui/material';
import { memo, useCallback, useEffect, useRef } from 'react';
import { animate } from 'framer-motion';

interface GlowingEffectProps {
	blur?: number;
	inactiveZone?: number;
	proximity?: number;
	spread?: number;
	variant?: 'default' | 'white';
	glow?: boolean;
	className?: string;
	disabled?: boolean;
	movementDuration?: number;
	borderWidth?: number;
	children?: React.ReactNode;
}

const StyledGlowContainer = styled(Box, {
	shouldForwardProp: (prop) => !['blur', 'spread', 'borderWidth', 'variant'].includes(prop as string),
})<{ 
	blur: number; 
	spread: number; 
	borderWidth: number;
	variant: 'default' | 'white';
}>(({ blur, spread, borderWidth, variant }) => ({
	position: 'relative',
	'--blur': `${blur}px`,
	'--spread': spread,
	'--start': '0',
	'--active': '0',
	'--glowingeffect-border-width': `${borderWidth}px`,
	'--repeating-conic-gradient-times': '5',
	'--gradient': variant === 'white' 
		? `repeating-conic-gradient(
			from 236.84deg at 50% 50%,
			#000,
			#000 calc(25% / var(--repeating-conic-gradient-times))
		)`
		: `radial-gradient(circle, #4786FF 10%, rgba(71, 134, 255, 0) 20%),
			radial-gradient(circle at 40% 40%, #3b82f6 5%, rgba(59, 130, 246, 0) 15%),
			radial-gradient(circle at 60% 60%, #1e40af 10%, rgba(30, 64, 175, 0) 20%),
			radial-gradient(circle at 40% 60%, #2563eb 10%, rgba(37, 99, 235, 0) 20%),
			repeating-conic-gradient(
				from 236.84deg at 50% 50%,
				#4786FF 0%,
				#3b82f6 calc(25% / var(--repeating-conic-gradient-times)),
				#1e40af calc(50% / var(--repeating-conic-gradient-times)),
				#2563eb calc(75% / var(--repeating-conic-gradient-times)),
				#4786FF calc(100% / var(--repeating-conic-gradient-times))
			)`,

	'& .glow-border': {
		position: 'absolute',
		inset: 0,
		borderRadius: 'inherit',
		opacity: 1,
		transition: 'opacity 0.3s ease',
		pointerEvents: 'none',
		filter: blur > 0 ? `blur(var(--blur))` : 'none',

		'&::after': {
			content: '""',
			borderRadius: 'inherit',
			position: 'absolute',
			inset: 'calc(-1 * var(--glowingeffect-border-width))',
			border: 'var(--glowingeffect-border-width) solid transparent',
			background: 'var(--gradient)',
			backgroundAttachment: 'fixed',
			opacity: 'var(--active)',
			transition: 'opacity 0.3s ease',
			WebkitMaskClip: 'padding-box, border-box',
			WebkitMaskComposite: 'intersect',
			maskClip: 'padding-box, border-box',
			maskComposite: 'intersect',
			WebkitMaskImage: `linear-gradient(transparent, transparent),
				conic-gradient(from calc((var(--start) - var(--spread)) * 1deg), 
					transparent 0deg, 
					white, 
					transparent calc(var(--spread) * 2deg))`,
			maskImage: `linear-gradient(transparent, transparent),
				conic-gradient(from calc((var(--start) - var(--spread)) * 1deg), 
					transparent 0deg, 
					white, 
					transparent calc(var(--spread) * 2deg))`,
		},
	},

	'& .inactive-border': {
		position: 'absolute',
		inset: '-1px',
		borderRadius: 'inherit',
		border: `1px solid ${variant === 'white' ? '#ffffff' : 'rgba(71, 134, 255, 0.2)'}`,
		opacity: 'calc(1 - var(--active))',
		transition: 'opacity 0.3s ease',
		pointerEvents: 'none',
	},
}));

const GlowingEffect = memo(({
	blur = 0,
	inactiveZone = 0.7,
	proximity = 0,
	spread = 20,
	variant = 'default',
	glow = false,
	className,
	movementDuration = 2,
	borderWidth = 1,
	disabled = true,
	children,
}: GlowingEffectProps) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const lastPosition = useRef({ x: 0, y: 0 });
	const animationFrameRef = useRef<number>(0);

	const handleMove = useCallback((e?: MouseEvent | { x: number; y: number }) => {
		if (!containerRef.current) return;

		if (animationFrameRef.current) {
			cancelAnimationFrame(animationFrameRef.current);
		}

		animationFrameRef.current = requestAnimationFrame(() => {
			const element = containerRef.current;
			if (!element) return;

			const { left, top, width, height } = element.getBoundingClientRect();
			const mouseX = e?.x ?? lastPosition.current.x;
			const mouseY = e?.y ?? lastPosition.current.y;

			if (e) {
				lastPosition.current = { x: mouseX, y: mouseY };
			}

			const center = [left + width * 0.5, top + height * 0.5];
			const distanceFromCenter = Math.hypot(mouseX - center[0], mouseY - center[1]);
			const inactiveRadius = 0.5 * Math.min(width, height) * inactiveZone;

			if (distanceFromCenter < inactiveRadius) {
				element.style.setProperty('--active', '0');
				return;
			}

			const isActive = 
				mouseX > left - proximity &&
				mouseX < left + width + proximity &&
				mouseY > top - proximity &&
				mouseY < top + height + proximity;

			element.style.setProperty('--active', isActive ? '1' : '0');

			if (!isActive) return;

			const currentAngle = parseFloat(element.style.getPropertyValue('--start')) || 0;
			const targetAngle = (180 * Math.atan2(mouseY - center[1], mouseX - center[0])) / Math.PI + 90;

			const angleDiff = ((targetAngle - currentAngle + 180) % 360) - 180;
			const newAngle = currentAngle + angleDiff;

			animate(currentAngle, newAngle, {
				duration: movementDuration,
				ease: [0.16, 1, 0.3, 1],
				onUpdate: (value) => {
					element.style.setProperty('--start', String(value));
				},
			});
		});
	}, [inactiveZone, proximity, movementDuration]);

	useEffect(() => {
		if (disabled) return;

		const handleScroll = () => handleMove();
		const handlePointerMove = (e: PointerEvent) => handleMove(e);

		window.addEventListener('scroll', handleScroll, { passive: true });
		document.body.addEventListener('pointermove', handlePointerMove, { passive: true });

		return () => {
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
			}
			window.removeEventListener('scroll', handleScroll);
			document.body.removeEventListener('pointermove', handlePointerMove);
		};
	}, [handleMove, disabled]);

	return (
		<StyledGlowContainer
			ref={containerRef}
			blur={blur}
			spread={spread}
			borderWidth={borderWidth}
			variant={variant}
			className={className}
			sx={{
				borderRadius: 'inherit',
				position: 'absolute',
				inset: 0,
				pointerEvents: 'none',
			}}
		>
			<Box className="inactive-border" sx={{ display: disabled ? 'block' : 'none' }} />
			<Box 
				className="glow-border" 
				sx={{ display: disabled ? 'none' : 'block', opacity: glow ? 1 : 0 }}
			/>
			{children}
		</StyledGlowContainer>
	);
});

GlowingEffect.displayName = 'GlowingEffect';

export { GlowingEffect };